import { Clock } from 'lucide-react';
import { useMemo, useState } from 'react';
import { api } from '../api/client';
import type { SupportSettings } from '../api/types';
import { ago } from '../lib/format';
import { DAYS, type DayDraft, draftFromHours, type HoursDraft, hoursFromDraft, timeZones } from '../lib/support';
import type { SupportDay } from '../api/types';

/**
 * The support desk's hours — what every operator's "Talk to a person" card
 * says about whether anyone is at a desk right now.
 *
 * They are STORED in sphere-crm, beside the panel that reads them, so the card
 * stays right while this console is down; this is only where they are edited,
 * and the CRM validates every save. Hours change the words an operator reads,
 * never whether their request is taken: out of hours the card says the desk is
 * closed and when it opens, and the request still lands on the queue.
 */

/** "Mon 9:00 AM", in the reader's own zone — the summary line carries the desk's. */
const when = (iso: string) =>
  new Date(iso).toLocaleString(undefined, { weekday: 'short', hour: 'numeric', minute: '2-digit' });

const browserZone = () => {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';
  } catch {
    return 'UTC';
  }
};

export function SupportDesk({
  settings,
  error,
  canEdit,
  onSaved,
}: {
  settings: SupportSettings | null;
  error: string | null;
  canEdit: boolean;
  onSaved: (next: SupportSettings) => void;
}) {
  const [editing, setEditing] = useState(false);

  // Only when there is nothing to show: a failed re-read keeps the hours it
  // had, with the failure said beneath them.
  if (error && !settings) {
    return (
      <div className="card card-pad support-desk">
        <div className="support-desk-head">
          <Clock size={15} />
          <strong>Support desk</strong>
        </div>
        <p className="note note-warn">Could not read the desk's hours from sphere-crm: {error}</p>
      </div>
    );
  }
  if (!settings) return null;

  const { now } = settings;
  const open = now.onCall === 'staffed';
  let status = 'not taking live chats on any day';
  if (open) status = now.closesAt ? `until ${when(now.closesAt)}` : 'no hours set, so always shown as open';
  else if (now.nextOpenAt) status = `opens ${when(now.nextOpenAt)}`;

  return (
    <div className="card card-pad support-desk">
      <div className="support-desk-head">
        <Clock size={15} />
        <strong>Support desk</strong>
        <span className={`badge ${open ? 'badge-ok' : 'badge-warn'}`}>{open ? 'Open now' : 'Closed'}</span>
        <span className="assist-muted">{status}</span>
        <span className="wt-spacer" />
        {canEdit && !editing && (
          <button type="button" className="btn btn-secondary" onClick={() => setEditing(true)}>
            Edit hours
          </button>
        )}
      </div>
      <p className="support-desk-summary">{settings.summary}</p>
      {error && <p className="note note-warn">Could not re-read the hours from sphere-crm: {error}. Shown as last read.</p>}
      {!settings.offered && (
        <p className="note">
          Operators are not being offered “Talk to a person” yet — <code>HELP_HANDOFF_ENABLED</code> is off in
          sphere-crm. Hours can be set now and take effect when it is turned on.
        </p>
      )}
      {settings.updatedBy && (
        <p className="assist-muted support-desk-meta">
          Last changed by {settings.updatedBy} {ago(settings.updatedAt)}
        </p>
      )}
      {editing && (
        <HoursEditor
          settings={settings}
          onCancel={() => setEditing(false)}
          onSaved={(next) => {
            setEditing(false);
            onSaved(next);
          }}
        />
      )}
    </div>
  );
}

function HoursEditor({
  settings,
  onCancel,
  onSaved,
}: {
  settings: SupportSettings;
  onCancel: () => void;
  onSaved: (next: SupportSettings) => void;
}) {
  const zones = useMemo(timeZones, []);
  const [draft, setDraft] = useState<HoursDraft>(() => draftFromHours(settings.hours, browserZone()));
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const checked = hoursFromDraft(draft);

  const setDay = (key: SupportDay, patch: Partial<DayDraft>) =>
    setDraft((current) => ({ ...current, days: { ...current.days, [key]: { ...current.days[key], ...patch } } }));

  const copyMonday = () =>
    setDraft((current) => {
      const days = { ...current.days };
      for (const key of ['tue', 'wed', 'thu', 'fri'] as SupportDay[]) days[key] = { ...current.days.mon };
      return { ...current, days };
    });

  const save = async (hours: SupportSettings['hours']) => {
    setSaving(true);
    setError(null);
    try {
      onSaved(await api.post<SupportSettings>('/support/settings/hours', { hours }));
    } catch (caught) {
      // The CRM's own sentence: it names the day and what is wrong with it.
      setError(caught instanceof Error ? caught.message : String(caught));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="support-hours">
      <label className="field">
        <span className="field-label">Time zone</span>
        <input
          className="input support-hours-zone"
          list="support-zones"
          value={draft.timezone}
          spellCheck={false}
          onChange={(e) => setDraft((current) => ({ ...current, timezone: e.target.value }))}
        />
        <datalist id="support-zones">
          {zones.map((zone) => (
            <option key={zone} value={zone} />
          ))}
        </datalist>
        <span className="field-hint">
          The hours below are in this zone. Operators are told when the desk opens in their own time.
        </span>
      </label>

      <div className="support-hours-grid">
        {DAYS.map(({ key, label }) => {
          const day = draft.days[key];
          return (
            <div key={key} className="support-hours-row">
              <label className="support-hours-day">
                <input type="checkbox" checked={day.open} onChange={(e) => setDay(key, { open: e.target.checked })} />
                {label}
              </label>
              {day.open ? (
                <>
                  <input
                    type="time"
                    className="input support-hours-time"
                    aria-label={`${label} opens`}
                    value={day.from}
                    onChange={(e) => setDay(key, { from: e.target.value })}
                  />
                  <span className="assist-muted">to</span>
                  <input
                    type="time"
                    className="input support-hours-time"
                    aria-label={`${label} closes`}
                    value={day.to}
                    onChange={(e) => setDay(key, { to: e.target.value })}
                  />
                </>
              ) : (
                <span className="assist-muted">Closed</span>
              )}
            </div>
          );
        })}
      </div>

      {'error' in checked && <p className="field-error">{checked.error}</p>}
      {error && <p className="note note-danger">{error}</p>}

      <div className="support-hours-actions">
        <button type="button" className="btn btn-ghost" onClick={copyMonday}>
          Copy Monday to Tue–Fri
        </button>
        <span className="wt-spacer" />
        {/* Not "closed": with no hours at all the desk reads as always open,
            which is a choice, so the button says what it does. */}
        <button type="button" className="btn btn-ghost" disabled={saving} onClick={() => void save(null)}>
          Remove hours (always open)
        </button>
        <button type="button" className="btn btn-secondary" disabled={saving} onClick={onCancel}>
          Cancel
        </button>
        <button
          type="button"
          className="btn btn-primary"
          disabled={saving || 'error' in checked}
          onClick={() => {
            if ('hours' in checked) void save(checked.hours);
          }}
        >
          {saving ? 'Saving…' : 'Save hours'}
        </button>
      </div>
    </div>
  );
}
